import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";

import { useAuth } from "../../context/AuthContext";
import useAxios from "../../hooks/useAxios";

import DashboardFilters from "../../components/Dashboard/DashboardFilters";
import DashboardSummaryCards from "../../components/Dashboard/DashboardSummaryCards";
import DashboardZoneCharts from "../../components/Dashboard/DashboardZoneCharts";
import DashboardServiceOrdersChart from "../../components/Dashboard/DashboardServiceOrdersChart";
import DashboardAmountAndTypeCharts from "../../components/Dashboard/DashboardAmountAndTypeCharts";
import DashboardAmountByServiceChart from "../../components/Dashboard/DashboardAmountByServiceChart";
import DashboardTopItemsChart from "../../components/Dashboard/DashboardTopItemsChart";
import DashboardOrdersSent from "../../components/Dashboard/DashboardOrdersSent";
import useSoftServices from "../../hooks/useSoftServices";
import useSoftZones from "../../hooks/useSoftZones";

const Dashboard = () => {
    const componentRef = useRef(null);

    const [filterBy, setFilterBy] = useState('services');
    const [filterDatesBy, setFilterDatesBy] = useState('month');

    const [filters, setFilters] = useState({
        monthAndYear: `${format(new Date(), 'yyyy-MM')}`,
        start: '',
        end: '',
        serviceIds: [],
        zoneIds: []
    });

    const [additionalFilters, setAdditionalFilters] = useState({
        orderTypeIds: []
    });

    const [{ data: dashBoardSummary, loading }, getDashBoardSummary] = useAxios({ url: `/dashboard`, params: { ...filters } }, { manual: true, useCache: false });

    const [{ data: ordersSentData, loading: ordersSentLoading }, getOrdersSentData] = useAxios({ url: `/reports/orders-sent`, params: { ...filters, ...additionalFilters } }, { manual: true, useCache: false });

    // Hook para la descarga del PDF
    const [{ loading: pdfLoading }, getExportPDF] = useAxios(
        { url: `/reports/export/pdf`, method: 'GET', responseType: 'blob' },
        { manual: true, useCache: false }
    );

    // Hook para la descarga del Excel
    const [{ loading: excelLoading }, getExportExcel] = useAxios(
        { url: `/reports/export/excel`, method: 'GET', responseType: 'blob' },
        { manual: true, useCache: false }
    );

    const [{ services, loading: servicesLoading }, getServices] = useSoftServices({ params: { page: 1 }, options: { useCache: false } });

    const [{ zones, loading: zonesLoading }, getZones] = useSoftZones({ params: { page: 1 }, options: { useCache: false } });

    useEffect(() => {
        if (filterDatesBy === 'month') {
            setFilters((oldFilters) => {
                return {
                    ...oldFilters,
                    start: '',
                    end: '',
                    monthAndYear: `${format(new Date(), 'yyyy-MM')}`
                }
            })
        }

        if (filterDatesBy === 'dates') {
            setFilters((oldFilters) => {
                return {
                    ...oldFilters,
                    monthAndYear: '',
                    start: `${format(new Date(), 'yyyy-MM-dd')}`,
                    end: `${format(new Date(), 'yyyy-MM-dd')}`
                }
            })
        }
    }, [filterDatesBy])

    useEffect(() => {
        if (filterBy === 'services') {
            setFilters((oldFilters) => {
                return {
                    ...oldFilters,
                    serviceIds: services?.map(service => service.id),
                    zoneIds: []
                }
            })
        }

        if (filterBy == 'zones') {
            setFilters((oldFilters) => {
                return {
                    ...oldFilters,
                    zoneIds: zones?.map(zone => zone.id),
                    serviceIds: []
                }
            });
        }
    }, [filterBy])

    useEffect(() => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                serviceIds: services?.map(service => service.id)
            }
        })
    }, [services])

    useEffect(() => {
        if (filters?.serviceIds?.length > 0 || filters?.zoneIds?.length > 0) {
            getDashBoardSummary({
                params: {
                    ...filters,
                    serviceIds: filters?.serviceIds.join(','),
                    zoneIds: filters?.zoneIds.join(',')
                }
            });
            getOrdersSentData({
                params: {
                    ...filters,
                    serviceIds: filters?.serviceIds.join(','),
                    zoneIds: filters?.zoneIds.join(',')
                }
            });
        }
    }, [filters]);

    const handleService = (e) => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                serviceIds: e.target.value ? [e.target.value] : services?.map(service => service.id)
            }
        });
    }

    const handleZone = (e) => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                zoneIds: e.target.value ? [e.target.value] : zones?.map(zone => zone.id)
            }
        })
    }

    // Función para manejar la descarga del PDF
    const handleExportPDF = async () => {
        try {
            const requestParams = {
                ...filters,
                serviceIds: filters?.serviceIds?.join(','),
                zoneIds: filters?.zoneIds?.join(',')
            };

            const response = await getExportPDF({ params: requestParams });
            const blob = new Blob([response.data || response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `dashboard_reporte_${format(new Date(), 'yyyy-MM-dd')}.pdf`);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error al descargar el PDF desde el servidor:", error);
        }
    };

    // Función para manejar la descarga del Excel
    const handleExportExcel = async () => {
        try {
            const requestParams = {
                ...filters,
                serviceIds: filters?.serviceIds?.join(','),
                zoneIds: filters?.zoneIds?.join(',')
            };

            const response = await getExportExcel({ params: requestParams });
            // Utilizamos el MIME type correspondiente a Excel (.xlsx)
            const blob = new Blob([response.data || response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `dashboard_reporte_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error al descargar el Excel desde el servidor:", error);
        }
    };

    const getReportTitle = () => {
        if (filterDatesBy === 'month' && filters?.monthAndYear) {
            const [year, month] = filters.monthAndYear.split('-');
            const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
            const nombreMes = meses[parseInt(month, 10) - 1];
            return `Reporte de pedidos de ${nombreMes} de ${year}`;
        }

        if (filterDatesBy === 'dates' && filters?.start && filters?.end) {
            const formatDate = (dateString) => {
                if (!dateString) return '';
                const [y, m, d] = dateString.split('-');
                return `${d}-${m}-${y}`;
            };
            return `Reporte de pedidos desde el ${formatDate(filters.start)} al ${formatDate(filters.end)}`;
        }

        return 'Reporte de pedidos';
    };

    return (
        <div>
            <DashboardFilters
                filterBy={filterBy} setFilterBy={setFilterBy}
                filterDatesBy={filterDatesBy} setFilterDatesBy={setFilterDatesBy}
                filters={filters} setFilters={setFilters}
                services={services} zones={zones}
                handleService={handleService} handleZone={handleZone}
            />

            {/* Contenedor de botones de exportación */}
            <div className="row mb-3 mt-2">
                <div className="col-12 d-flex justify-content-end gap-2">

                    <button
                        className="btn btn-success text-white shadow-sm mr-2"
                        onClick={handleExportExcel}
                        disabled={loading || ordersSentLoading || pdfLoading || excelLoading}
                    >
                        <i className="fa fa-file-excel"></i>
                        {excelLoading ? ' Descargando...' : ' Descargar Excel'}
                    </button>

                    <button
                        className="btn btn-danger text-white shadow-sm"
                        onClick={handleExportPDF}
                        disabled={loading || ordersSentLoading || pdfLoading || excelLoading}
                    >
                        <i className="fa fa-file-pdf"></i>
                        {pdfLoading ? ' Descargando...' : ' Descargar PDF'}
                    </button>

                </div>
            </div>

            <div ref={componentRef} className="p-3 bg-white">
                <h2 className="text-center mb-4 text-dark font-weight-bold">
                    {getReportTitle()}
                </h2>

                <DashboardSummaryCards dashBoardSummary={dashBoardSummary} loading={loading} />
                <DashboardZoneCharts dashBoardSummary={dashBoardSummary} loading={loading} />
                <DashboardServiceOrdersChart dashBoardSummary={dashBoardSummary} loading={loading} />
                <DashboardAmountAndTypeCharts dashBoardSummary={dashBoardSummary} loading={loading} />
                <DashboardAmountByServiceChart dashBoardSummary={dashBoardSummary} loading={loading} />
                <DashboardTopItemsChart dashBoardSummary={dashBoardSummary} loading={loading} />

                <DashboardOrdersSent
                    ordersSentData={ordersSentData}
                    loading={ordersSentLoading}
                    filters={filters}
                    setFilters={setFilters}
                />
            </div>
            <br /><br />
        </div>
    )
}

export default Dashboard;